import React from "react";
import { Link } from "react-router-dom";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Building, Building2, ChevronDown, Check, Plus, AlertCircle } from "lucide-react";

/**
 * Tiny circular avatar used inside the switcher buttons + dropdown items.
 * Renders the org's uploaded logo when present, falls back to a tinted icon.
 */
function OrgAvatar({ logo, kind, size = "sm" }) {
  const dim = size === "md" ? "w-6 h-6" : "w-5 h-5";
  const iconSize = size === "md" ? "w-3.5 h-3.5" : "w-3 h-3";
  const tint = kind === "school"
    ? "bg-[#DBEAFE] text-[#2563EB]"
    : "bg-[#FFE0B2] text-[#E65100]";
  const Icon = kind === "school" ? Building : Building2;
  return (
    <span
      className={`${dim} rounded-full overflow-hidden flex items-center justify-center shrink-0 ${logo ? "" : tint}`}
      aria-hidden
    >
      {logo
        ? <img src={logo} alt="" className="w-full h-full object-cover" />
        : <Icon className={iconSize} />}
    </span>
  );
}

export default function SchoolBranchSwitcher() {
  const {
    approvedSchools, branchesForSelectedSchool,
    selectedSchool, selectedBranch,
    selectedSchoolId, selectedBranchId,
    setSelectedSchoolId, setSelectedBranchId,
    loading,
  } = useBranch();
  const { user } = useAuth();
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";

  if (loading) return null;

  if (approvedSchools.length === 0) {
    if (!isOwner) return null;   // Staff with no school assigned: render nothing.
    return (
      <Link to="/school-profile" data-testid="switcher-register-school">
        <Button
          variant="outline"
          className="rounded-full border-brand-purple/30 text-brand-purple hover:bg-brand-purpleLight h-9"
        >
          <Plus className="w-4 h-4 mr-1" />
          <span className="hidden sm:inline">Register a school</span>
          <span className="sm:hidden">Register</span>
        </Button>
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-2 min-w-0" data-testid="branch-switcher">
      {/* School dropdown — staff with only one school just see a static label */}
      {(isOwner || approvedSchools.length > 1) ? (
        <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="rounded-full border-[#2563EB]/20 bg-white shadow-sm hover:bg-[#EFF6FF] text-foreground h-10 px-3 max-w-[180px] md:max-w-none font-medium"
            data-testid="school-switcher-trigger"
          >
            <OrgAvatar logo={selectedSchool?.logo_base64} kind="school" size="md" />
            <span className="ml-2 truncate md:overflow-visible">{selectedSchool?.name || "Select school"}</span>
            <ChevronDown className="w-3.5 h-3.5 ml-1 shrink-0 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="rounded-2xl border-brand-purple/20 w-64"
          data-testid="school-switcher-menu"
        >
          <DropdownMenuLabel className="font-heading text-xs text-brand-inkMuted">Your schools</DropdownMenuLabel>
          {approvedSchools.map((s) => (
            <DropdownMenuItem
              key={s.id}
              onSelect={() => setSelectedSchoolId(s.id)}
              className="rounded-xl cursor-pointer font-body"
              data-testid={`school-option-${s.id}`}
            >
              <OrgAvatar logo={s.logo_base64} kind="school" />
              <span className="flex-1 truncate ml-2">{s.name}</span>
              {s.id === selectedSchoolId && <Check className="w-4 h-4 text-brand-purple" />}
            </DropdownMenuItem>
          ))}
          {isOwner && <DropdownMenuSeparator />}
          {isOwner && (
            <DropdownMenuItem asChild className="rounded-xl cursor-pointer font-body">
              <Link to="/school-profile" data-testid="switcher-add-school">
                <Plus className="w-4 h-4 mr-2 text-brand-purple" />
                Register new school
              </Link>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      ) : (
        <div className="flex items-center rounded-full bg-white/70 border border-[#2563EB]/20 h-10 px-3 max-w-[180px] md:max-w-none font-medium text-foreground shadow-sm" data-testid="single-school-label">
          <OrgAvatar logo={selectedSchool?.logo_base64} kind="school" size="md" />
          <span className="ml-2 truncate md:overflow-visible">{selectedSchool?.name}</span>
        </div>
      )}

      {/* Branch dropdown — staff assigned to only one branch see a static
          label (no clickable dropdown). Owners always get the dropdown so
          they can add new branches via the "+ Add branch" item. */}
      {branchesForSelectedSchool.length > 0 ? (
        (isOwner || branchesForSelectedSchool.length > 1) ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="rounded-full border-[#2563EB]/20 bg-white shadow-sm hover:bg-[#EFF6FF] text-foreground h-10 px-3 max-w-[160px] md:max-w-none font-medium"
              data-testid="branch-switcher-trigger"
            >
              <OrgAvatar logo={selectedBranch?.logo_base64} kind="branch" size="md" />
              <span className="ml-2 truncate md:overflow-visible">{selectedBranch?.name || "Select branch"}</span>
              <ChevronDown className="w-3.5 h-3.5 ml-1 shrink-0 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="rounded-2xl border-brand-purple/20 w-64"
            data-testid="branch-switcher-menu"
          >
            <DropdownMenuLabel className="font-heading text-xs text-brand-inkMuted">
              Branches of {selectedSchool?.name}
            </DropdownMenuLabel>
            {branchesForSelectedSchool.map((b) => (
              <DropdownMenuItem
                key={b.id}
                onSelect={() => setSelectedBranchId(b.id)}
                className="rounded-xl cursor-pointer font-body"
                data-testid={`branch-option-${b.id}`}
              >
                <OrgAvatar logo={b.logo_base64} kind="branch" />
                <span className="flex-1 truncate ml-2">{b.name}</span>
                {b.id === selectedBranchId && <Check className="w-4 h-4 text-brand-purple" />}
              </DropdownMenuItem>
            ))}
            {isOwner && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="rounded-xl cursor-pointer font-body">
                  <Link to="/branches" data-testid="switcher-add-branch">
                    <Plus className="w-4 h-4 mr-2 text-brand-purple" />
                    Add branch
                  </Link>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        ) : (
          <div
            className="flex items-center rounded-full bg-white/70 border border-[#2563EB]/20 h-10 px-3 max-w-[160px] md:max-w-none font-medium text-foreground shadow-sm"
            data-testid="single-branch-label"
            title="Your account is assigned to a single branch"
          >
            <OrgAvatar logo={selectedBranch?.logo_base64} kind="branch" size="md" />
            <span className="ml-2 truncate md:overflow-visible">{selectedBranch?.name}</span>
          </div>
        )
      ) : (
        <Link to="/branches" data-testid="switcher-no-branch">
          <Button
            variant="outline"
            className="rounded-full border-[#FFB74D] text-[#E65100] hover:bg-[#FFF3E0] h-9"
          >
            <AlertCircle className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">No approved branch</span>
            <span className="sm:hidden">No branch</span>
          </Button>
        </Link>
      )}
    </div>
  );
}
